import type { FC } from 'react';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { LowQuotaNotificationBanner } from 'components/quota-components/LowQuotaNotificationBanner';
import { useIsDataCreditsEnabled } from 'features/quota/hooks/useIsDataCreditsEnabled';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useUserStatus } from 'hooks/useUserStatus';

import type { Company } from '../../../domain/company';
import { Crm } from '../../../domain/crm';
import type { OpportunityList } from '../../../domain/list';
import { useExportAccountToCrmMutation } from '../../../features/crm/exportAccountToCrm/exportAccountToCrmMutation';
import { useCrmStatusQuery } from '../../../features/crm/useCrmStatusQuery';
import { useSaveDomainToListMutation } from '../../../features/opportunityLists/saveDomainToList/useSaveDomainToListMutation';
import { useLowQuotaNotificationConfig } from '../../../features/quota/hooks/useLowQuotaNotificationConfig';
import { CompanyContactUsExportOption } from '../CompanyContactUsExportOption/CompanyContactUsExportOption';
import { CompanyExportToCrmContainer } from '../CompanyExportToCrmContainer';
import { CompanySaveToListContainer } from '../CompanySaveToListContainer';
import { CrmInfoContainer } from '../CrmInfoContainer';
import { SavedListsInfoContainer } from '../SavedListsInfoContainer';
import { SaveToListModal } from '../SaveToListModal';

import { StyledActionButton, StyledActionButtonWrapper, StyledDropdown } from './styles';
import { useCompanyActionTracking } from './useCompanyActionTracking';

export const CompanyActionsContainer: FC<{ company: Company }> = ({ company }) => {
  const { domain } = company;
  const { t } = useTranslation();

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isSaveToListModalOpened, setIsSaveToListModalOpened] = useState(false);

  const exportAccountToCrmMutation = useExportAccountToCrmMutation();
  const saveDomainToListMutation = useSaveDomainToListMutation();

  useCrmStatusQuery(Crm.hubspot);
  useCrmStatusQuery(Crm.salesforce);

  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();
  const { quota, canAfford, getExportCost } = useQuota();

  const { isNoTouch } = useUserStatus();
  const isAnyExportUnaffordable = !canAfford(getExportCost(Crm.salesforce)) || !canAfford(getExportCost(Crm.hubspot));

  // No-touch users: show banner when any CRM export is unaffordable (ignores isLow threshold)
  // Other users: show banner when quota is low / operation unaffordable
  const shouldShowBanner = isNoTouch ? isAnyExportUnaffordable : quota.isLow || isAnyExportUnaffordable;

  const { handleTrackOpenDropdown, handleTrackListAction, handleTrackOnSaveToList, handleTrackExportAction } =
    useCompanyActionTracking();

  const lowQuotaNotificationConfig = useLowQuotaNotificationConfig();

  const handleOpenDropdown = () => {
    setIsDropdownOpened(true);
    handleTrackOpenDropdown();
  };

  const handleOpenSaveToListModal = () => {
    handleTrackListAction('open save to list options', 'save to list options');
    setIsSaveToListModalOpened(true);
    setIsDropdownOpened(false);
  };

  const handleCloseSaveToListModal = () => {
    handleTrackListAction('cancel export', 'cancel save to list');
    setIsSaveToListModalOpened(false);
  };

  const handleSaveToList = (list: OpportunityList) => {
    setIsSaveToListModalOpened(false);
    setIsDropdownOpened(false);
    handleTrackOnSaveToList(list);
    saveDomainToListMutation.mutate({ list, domain });
  };

  const handleExportToCrm = (crm: Crm) => {
    handleTrackExportAction('export company', crm);
    setIsDropdownOpened(false);
    exportAccountToCrmMutation.mutate({
      domain,
      crm,
      onSuccess: () => handleTrackExportAction('export success', crm),
      onError: () => handleTrackExportAction('export failed', crm),
    });
  };

  const isLoading = exportAccountToCrmMutation.isPending || saveDomainToListMutation.isPending;

  return (
    <StyledActionButtonWrapper>
      <StyledActionButton
        onClick={handleOpenDropdown}
        isLoading={isLoading}
        isActive={isDropdownOpened}
        data-automation="ActionButton"
      >
        {isLoading ? (
          <>
            {t(exportAccountToCrmMutation.isPending ? 'company.export_to_crm.loading' : 'company.save_to_list.loading')}
          </>
        ) : (
          <>
            {t('company.export_to')}
            <SWReactIcons iconName={isDropdownOpened ? 'chev-up' : 'chev-down'} size="xs" />
          </>
        )}
      </StyledActionButton>
      <CrmInfoContainer domain={domain} />
      <SavedListsInfoContainer company={company} />
      <StyledDropdown isOpened={isDropdownOpened} onClose={() => setIsDropdownOpened(false)} minWidth="100%">
        <CompanySaveToListContainer onOpenSaveToListModal={handleOpenSaveToListModal} />
        <CompanyExportToCrmContainer crm={Crm.salesforce} onExport={handleExportToCrm} />
        <CompanyExportToCrmContainer crm={Crm.hubspot} onExport={handleExportToCrm} />
        <CompanyContactUsExportOption />

        {isDataCreditsEnabled ? (
          <LowQuotaNotificationBanner
            isSuccess={lowQuotaNotificationConfig.isSuccess}
            isVisible={shouldShowBanner}
            title={lowQuotaNotificationConfig.title}
            description={lowQuotaNotificationConfig.description}
            buttonText={lowQuotaNotificationConfig.buttonText}
            onButtonClick={lowQuotaNotificationConfig.onButtonClick}
            isLoading={lowQuotaNotificationConfig.isLoading}
          />
        ) : null}
      </StyledDropdown>
      <SaveToListModal
        isOpen={isSaveToListModalOpened}
        onClose={handleCloseSaveToListModal}
        company={company}
        onSaveToList={handleSaveToList}
      />
    </StyledActionButtonWrapper>
  );
};
